import { Link } from "react-router-dom";
import Button from "../../../../form/Button";
import Table from "../../../../elements/table/Table";
import Search from "../../../../form/Search";
import { lngDetect } from "../../../../../helpers/lngDetect";
import { formatEventDate } from "../../../../../util/Flag";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { dynamicOrderList } from "../../../../../services/orderService";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { t } from "i18next";

export default function OrderDetails({ details }) {
  const [searchText, setSearchText] = useState("");
  const { lang } = useSelector((state) => state?.langProvider);
  const { session } = useSelector((state) => state?.authProvider);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  const eventData = details?.eventData;
  const eventCreator = details?.userData;
  const buyerData = details?.buyerInfo;
  const paymentData = details?.payment;

  const filteredItems = useMemo(() => {
    if (!Array.isArray(details?.orderItemData)) return [];

    return details.orderItemData.filter((item) => {
      const fullName = `${item?.attendeeInfo?.firstName ?? ""} ${item?.attendeeInfo?.lastName ?? ""}`.toLowerCase();
      const barcode = item?.uid?.toLowerCase() ?? "";
      const ticket = item?.ticketInfo?.name?.en?.toLowerCase() ?? "";
      const query = searchText.toLowerCase();

      return fullName.includes(query) || barcode.includes(query) || ticket.includes(query);
    });
  }, [searchText, details?.orderItemData]);

  const filteredResult = useMemo(() => {
    return Object.values(
      filteredItems.reduce((acc, item) => {
        const name = item.ticketInfo?.name?.en || "Unknown";
        if (!acc[name]) acc[name] = { name, count: 0 };
        acc[name].count += 1;
        return acc;
      }, {})
    );
  }, [filteredItems]);

  const { data: orderFormData } = useQuery({
    queryKey: ["orderForm", filters, details?._id],
    queryFn: async () => {
      const res = await dynamicOrderList({
        ...filters,
        typeId: details?._id,
      });
      if (res?.docs?.status !== 200) {
        toast.warning(res?.message);
      }

      return res?.docs;
    },
    enabled: !!details?._id,
  });

  // Process form data for display
  const processedFormData = useMemo(() => {
    if (!orderFormData?.data || !Array.isArray(orderFormData.data) || orderFormData.data.length === 0) {
      return [];
    }

    return orderFormData.data.map((form) => {
      const formType = form.formData?.type || "Custom Form";
      const formId = form.formData?.uid || form.formData?._id;

      return {
        id: form._id,
        formType: formType === "global-checkout" ? "Checkout Form" : "Custom Form",
        formId,
        answers:
          form.answers?.map((answer) => ({
            label: answer.fieldMeta?.label?.text?.en || "Unknown Field",
            value: answer.value,
            type: answer.fieldMeta?.type || "text",
            required: answer.fieldMeta?.required || false,
          })) || [],
      };
    });
  }, [orderFormData?.data]);

  const eventInfo = {
    title: lngDetect(eventData?.title) || "Demo Event",
    host: (eventCreator?.firstName ?? "") + " " + (eventCreator?.lastName ?? ""),
    details: [
      {
        icon: "fa-location-dot",
        text: [eventData?.address?.address1, eventData?.address?.state, eventData?.address?.country].filter(Boolean).join(", "),
        color: "text-primary",
      },
      {
        icon: "fa-calendar-days",
        text: formatEventDate(eventData?.startDate, eventData?.endDate),
        color: "text-primary",
      },
    ],
    orderNo: details?.orderId,
    status: details?.status?.info || "Pending",
    createdAt: details?.createdAt,
  };

  const orderSummary = {
    title: "Order Summary",
    links: [
      // { label: "Full Details", to: "#" },
      // { label: "Add Note", to: "#" },
    ],
    data: [
      {
        label: "Buyer Name",
        value: (buyerData?.firstName ?? "") + " " + (buyerData?.lastName ?? ""),
      },
      { label: "Order Total", value: `${details?.eventData?.currency?.symbol} ${details?.total.toFixed(2)}` },
      {
        label: "Payment",
        value: paymentData?.status.charAt(0).toUpperCase() + paymentData?.status.slice(1) || "Pending",
      },
      {
        label: "Ticket Count",
        value: (
          <ul className="grid grid-cols-3 gap-x-1 gap-y-2 list-disc list-outside pl-0">
            {filteredResult?.map((word, index) => (
              <li key={index} className="text-deep-purple text-sm ">
                {word?.count} {word?.name}
              </li>
            ))}
          </ul>
        ),
      },
    ],
  };

  const tableHeadData = [
    {
      _id: "barcode",
      name: "Barcode",
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "name",
      name: "Name",
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "ticket",
      name: "Ticket",
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
    {
      _id: "price",
      name: "Price",
      align: "left",
      isSort: false,
      isFilter: false,
      thClasses: "px-6 py-3",
    },
  ];

  const tableData = filteredItems?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: "barcode",
          type: "text",
          data: (
            <div>
              {item?.ticketInfo?.uid}
              <div className="text-xs text-slate-400">{item?.ticketInfo?.status}</div>
            </div>
          ),
          tdClasses: "px-6 py-4",
        },
        {
          _id: "name",
          type: "text",
          data: (item?.attendeeInfo?.firstName ?? "") + " " + (item?.attendeeInfo?.lastName ?? ""),
          tdClasses: "px-6 py-4",
        },
        {
          _id: "ticket",
          type: "text",
          data: (
            <div>
              <div className="max-h-40 overflow-y-auto">
                <p className="text-deep-purple text-sm ">{lngDetect(item?.ticketInfo?.name)}</p>
              </div>
              <div className="text-xs text-slate-400">eTicket</div>
            </div>
          ),
          tdClasses: "px-6 py-4",
        },
        {
          _id: "price",
          type: "text",
          data: (details?.eventData?.currency?.symbol ? details?.eventData?.currency?.symbol : "") + " " + item?.finalPrice,
          tdClasses: "px-6 py-4",
        },
      ],
    };
  });

  // Helper function to format field values based on type
  const formatFieldValue = (value, type) => {
    if (!value) return "Not provided";

    switch (type) {
      case "date":
        return new Date(value).toLocaleDateString();
      case "email":
        return (
          <a href={`mailto:${value}`} className="text-blue-600 hover:underline">
            {value}
          </a>
        );
      case "textarea":
        return (
          <div className="max-w-xs">
            <p className="whitespace-pre-wrap break-words">{value}</p>
          </div>
        );
      case "checkbox":
        // Handle checkbox arrays
        if (Array.isArray(value)) {
          return (
            <div className="flex flex-wrap gap-1">
              {value.map((item, index) => (
                <span key={index} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                  {item?.label?.text?.en || item?.label || "Unknown"}
                </span>
              ))}
            </div>
          );
        }
        // Fallback for single checkbox value
        return value?.label?.text?.en || value?.label || String(value);
      case "radio":
      case "select":
        // Handle radio/select objects
        if (typeof value === "object" && value !== null) {
          return (
            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">{value?.label?.text?.en || value?.label || "Unknown"}</span>
          );
        }
        return String(value);
      default:
        // Handle any remaining object types
        if (typeof value === "object" && value !== null) {
          if (Array.isArray(value)) {
            return (
              <div className="space-y-1">
                {value.map((item, index) => (
                  <span key={index} className="block">
                    {typeof item === "object" ? item?.label?.text?.en || item?.label || JSON.stringify(item) : String(item)}
                  </span>
                ))}
              </div>
            );
          }
          return value?.label?.text?.en || value?.label || JSON.stringify(value);
        }
        return String(value);
    }
  };

  return (
    <div className="w-full py-2 px-6 text-slate-800">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        <div className="bg-white/80 backdrop-blur rounded-3xl p-6 shadow-md sticky top-32 transition-all duration-300 hover:shadow-xl hover:shadow-primary/10 border border-transparent hover:border-primary/10 hover:no-underline">
          <div className="text-xs font-semibold uppercase tracking-wider text-primary mb-2">Event Information</div>
          <div className="text-2xl font-extrabold mb-1">{eventInfo.title}</div>
          <div className="text-sm text-slate-500 mb-6">Hosted by {eventInfo.host}</div>
          <div className="space-y-3">
            {eventInfo.details.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md bg-primary/10 flex-shrink-0 border border-primary/10 flex items-center justify-center">
                  <i className={`fas ${item.icon} ${item.color}`} />
                </div>
                <div className="text-slate-600 text-sm">{item.text}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 bg-gradient-to-br from-slate-50 to-white p-4 rounded-xl border border-slate-100 shadow-inner space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500 font-medium">Order No:</span>
              <span className="font-semibold text-slate-700">{eventInfo.orderNo}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500 font-medium">Status:</span>
              <span className="bg-gradient-red/10 text-gradient-red px-2 py-1 rounded-full text-xs font-medium capitalize">{eventInfo.status}</span>
            </div>
          </div>
          {session?.user?.role !== "admin" ? (
            <div className="mt-6">
              <Link
                to={`/${lang}/user/events/event-manage/${eventData?.uid}`}
                className="text-white text-sm font-medium bg-primary-gradient px-6 rounded-full hover:opacity-90 transition-all duration-300 py-3"
              >
                Manage Event →
              </Link>
            </div>
          ) : (
            <>
              <div className="text-xs font-semibold uppercase tracking-wider text-primary mt-5 mb-3">{t("Organization")}</div>
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 flex-shrink-0 rounded-xl overflow-hidden bg-gray-100 shadow">
                  {details?.eventData?.organizationData?.logo?.url ? (
                    <img
                      src={details?.eventData?.organizationData?.logo?.url}
                      alt={lngDetect(details?.eventData?.organizationData?.name)}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">No Image</div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 break-words leading-snug">
                    {lngDetect(details?.eventData?.organizationData?.name)}
                  </h3>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="lg:col-span-2">
          <div className="h-[calc(100vh-320px)] overflow-y-auto pr-2 scrollbar space-y-5">
            <div className="flex justify-between items-center gap-3 bg-slate-100 py-1 px-2 rounded-md">
              <h2 className="text-2xl font-bold whitespace-nowrap">{eventInfo.orderNo}</h2>
              <div className="text-sm text-slate-500 mt-1">{formatEventDate(eventInfo?.createdAt)}</div>
            </div>

            {/* Form Data Section - Only show if data exists */}
            {processedFormData.length > 0 && (
              <div className="bg-white/80 backdrop-blur rounded-3xl p-6 border border-slate-200 shadow">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold">Additional Information</h3>
                </div>

                <div className="space-y-6">
                  {processedFormData.map((form) => (
                    <div key={form.id} className="border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-slate-800">{form.formType}</h4>
                          {form.formId && <p className="text-xs text-slate-500 mt-1">ID: {form.formId}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {form.answers.map((answer, answerIndex) => (
                          <div key={answerIndex} className="space-y-1">
                            <div className="flex items-center gap-2">
                              <label className="text-sm font-medium text-slate-700">{answer.label}</label>
                              {answer.required && <span className="text-red-500 text-xs">*</span>}
                            </div>
                            <div className="text-sm text-slate-600">{formatFieldValue(answer.value, answer.type)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white/80 backdrop-blur rounded-3xl p-6 border border-slate-200 shadow">
              <h3 className="text-lg font-bold mb-4">Order Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                {orderSummary.data.map((item, idx) => (
                  <div className="relative" key={idx}>
                    <div className="text-slate-500">{item.label}</div>
                    <div className="font-medium text-slate-800">{item.value}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex gap-4 text-sm text-deep-purple">
                {orderSummary.links.map((link, idx) => (
                  <Link to={link.to} className="hover:underline" key={idx}>
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-white/80 backdrop-blur rounded-3xl p-6 border border-slate-200 shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">Attendees</h3>
              </div>
              <div className="relative mb-3">
                <Search
                  placeholder={"Search attendee, barcode, ticket name..."}
                  inputClasses={"!border-slate-300 !bg-slate-50"}
                  divClasses={"rounded-lg h-10 border-slate-300 border"}
                  searchValue={searchText}
                  search={(val) => setSearchText(val)}
                />
              </div>
              <div className="relative">
                <Table tableHeadData={tableHeadData} tableData={tableData} pageCount={1} currentPage={1} isLoder={false} isOrderingDisabled={true} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
